import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import { ConversationBalanceDynamics, ConversationDominance } from '../../../services/api.service';
import {
  SpeakerLabelIndex,
  describeBalanceTrend,
  orderLabels,
  resolveLabelMeta,
} from '../utils/voice-analytics.utils';
import { AnalyticsCard } from './meeting-analytics-panel.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type Props = {
  dominance: ConversationDominance | null;
  balanceDynamics: ConversationBalanceDynamics | null;
  labelIndex: SpeakerLabelIndex;
};

function bucketLabel(startSec: number, endSec: number): string {
  const startMin = Math.floor(startSec / 60);
  const endMin = Math.ceil(endSec / 60);
  return `${startMin}–${endMin}′`;
}

/** Задача B1: карточка «Доминирование и динамика» — полоски dominance.by_speaker +
 *  мини стек-бар по 5-минутным бакетам balance_dynamics. */
const DominanceBalanceCard: React.FC<Props> = ({ dominance, balanceDynamics, labelIndex }) => {
  const { t } = useTranslation();

  const dominanceRows = useMemo(() => {
    if (!dominance?.by_speaker) return [];
    return Object.entries(dominance.by_speaker)
      .map(([label, value], i) => ({ meta: resolveLabelMeta(label, labelIndex, i), value: value || 0 }))
      .sort((a, b) => b.value - a.value);
  }, [dominance, labelIndex]);

  const buckets = useMemo(() => balanceDynamics?.buckets || [], [balanceDynamics]);

  const bucketSpeakers = useMemo(
    () => orderLabels(
      new Set(buckets.flatMap(b => Object.keys(b.by_speaker_percent))),
      labelIndex,
    ),
    [buckets, labelIndex],
  );

  const trend = useMemo(
    () => describeBalanceTrend(buckets, dominance?.most_dominant ?? null),
    [buckets, dominance],
  );

  const trendMeta = dominance?.most_dominant ? resolveLabelMeta(dominance.most_dominant, labelIndex) : null;

  if (!dominanceRows.length && !buckets.length) return null;

  return (
    <AnalyticsCard title={t('voice-analytics.dominance-title')} badge="data">
      <p className={cn('analytics-hint')}>{t('voice-analytics.dominance-hint')}</p>

      {dominanceRows.length > 0 && (
        <div className={cn('speaker-tones-list')}>
          {dominanceRows.map(row => (
            <div key={row.meta.label} className={cn('speaker-tones-row')}>
              <div className={cn('speaker-tones-label')}>
                <span className={cn('speaker-tones-dot')} style={{ backgroundColor: row.meta.color }} />
                <span className={cn('speaker-tones-name')} title={row.meta.displayName}>
                  {row.meta.shortName}
                </span>
              </div>
              <div className={cn('speaker-tones-bar-track')}>
                <div
                  className={cn('speaker-tones-bar-fill')}
                  style={{ width: `${Math.max(row.value * 100, 3)}%`, backgroundColor: row.meta.color }}
                />
              </div>
              <span className={cn('speaker-tones-badge')} style={{ color: row.meta.color, backgroundColor: `${row.meta.color}18` }}>
                {Math.round(row.value * 100)}
              </span>
            </div>
          ))}
        </div>
      )}

      {trend && trendMeta && (
        <p className={cn('analytics-subtitle')}>
          {t(`voice-analytics.balance-trend-${trend}`, { name: trendMeta.shortName })}
        </p>
      )}

      {buckets.length > 0 && (
        <div className={cn('voice-balance-list')}>
          {buckets.map(bucket => (
            <div key={`${bucket.start_seconds}-${bucket.end_seconds}`} className={cn('voice-balance-row')}>
              <span className={cn('voice-balance-label')}>
                {bucketLabel(bucket.start_seconds, bucket.end_seconds)}
              </span>
              <div className={cn('voice-balance-bar')}>
                {bucketSpeakers.map(label => {
                  const pct = bucket.by_speaker_percent[label];
                  if (!pct) return null;
                  const meta = resolveLabelMeta(label, labelIndex);
                  return (
                    <div
                      key={label}
                      className={cn('voice-balance-segment')}
                      style={{ width: `${pct}%`, backgroundColor: meta.color }}
                      title={`${meta.displayName}: ${Math.round(pct)}%`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </AnalyticsCard>
  );
};

export default DominanceBalanceCard;
